// The dev command console: help list (filtered to the caller's role) + input.
// Parse errors and ladder refusals render locally; everything else goes to
// the SECURITY DEFINER RPCs, which are the actual enforcers.
import type { SupabaseClient } from '@supabase/supabase-js';
import { COMMAND_HELP, commandRefusal, parseCommand } from './commands';
import {
  devBan,
  devGive,
  devMessage,
  devOp,
  devRemove,
  devRestock,
  devSkip,
} from './accountActions';
import { el } from './dom';
import type { Role } from './roles';

export function buildCommandConsole(client: SupabaseClient, role: Role): HTMLDivElement {
  const error = el('div', { className: 'account-error', testid: 'cmd-error' });
  const result = el('div', { className: 'account-notice', testid: 'cmd-result' });

  const help = el(
    'div',
    { className: 'cmd-help', testid: 'cmd-help' },
    ...COMMAND_HELP.filter((h) => h.visibleTo(role)).map((h) =>
      el(
        'div',
        { className: 'cmd-help-entry', testid: `cmd-help-${h.usage.split(' ')[0].slice(1)}` },
        el('code', { className: 'cmd-usage' }, h.usage),
        el('div', { className: 'cmd-desc' }, h.description),
        el('div', { className: 'cmd-example' }, 'Example: ', el('code', {}, h.example)),
      ),
    ),
  );

  const input = el('input', {
    type: 'text',
    maxLength: 240,
    placeholder: 'Type a command…',
    testid: 'cmd-input',
  });
  const run = el('button', { testid: 'cmd-run' }, 'Run');

  let busy = false;

  async function submit(): Promise<void> {
    if (busy) return;
    error.textContent = '';
    result.textContent = '';
    const parsed = parseCommand(input.value);
    if (!parsed.ok) {
      error.textContent = parsed.error;
      return;
    }
    const refusal = commandRefusal(role, parsed.command);
    if (refusal) {
      error.textContent = refusal;
      return;
    }
    busy = true;
    run.disabled = true;
    input.disabled = true;
    try {
      const c = parsed.command;
      if (c.cmd === 'ban') {
        const r = await devBan(client, c.target);
        if (!r.ok) {
          error.textContent = r.error;
          return;
        }
        result.textContent = `Banned ${r.banned}.`;
        input.value = '';
        return;
      }
      if (c.cmd === 'restock') {
        const r = await devRestock(client);
        if (!r.ok) {
          error.textContent = r.error;
          return;
        }
        result.textContent = 'Potion Shop restocked for everyone; the timer restarts at 10:00.';
        input.value = '';
        return;
      }
      const r =
        c.cmd === 'give'
          ? await devGive(client, c.target, c.what, c.value)
          : c.cmd === 'op'
            ? await devOp(client, c.target)
            : c.cmd === 'skip'
              ? await devSkip(client, c.target)
              : c.cmd === 'remove'
                ? await devRemove(client, c.target, c.what, c.value, c.amount)
                : await devMessage(client, c.target, c.text);
      if (!r.ok) {
        error.textContent = r.error;
        return;
      }
      result.textContent =
        r.delivered === 'global' ? 'Sent to everyone online.' : `Delivered to ${r.to}.`;
      input.value = '';
    } finally {
      busy = false;
      run.disabled = false;
      input.disabled = false;
    }
  }

  run.addEventListener('click', () => void submit());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void submit();
  });

  return el(
    'div',
    { className: 'cmd-console', testid: 'cmd-console' },
    el('h3', {}, 'Commands'),
    help,
    el('div', { className: 'cmd-inputrow' }, input, run),
    result,
    error,
  );
}
